import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import {
  MenuItemGroup,
  MenuMetadataForChannel,
  Menu,
  MenuItem,
  MealBuilderItemSection,
  BuilderMode,
  SelectableMenuItem,
  ItemCardCustomization,
  ItemCardCustomizationType,
  PortionItemType,
  SelectableVariantMenuItem,
  SelectableItemCardCustomization,
  MealBuilderMenuSection,
} from '@/models';

import { ItemSelectedEventArgs } from '../CmgMealBuilderItemSelector/cmg-meal-builder-item-selector';
import { joinWithSuffix, sortFn, partition, isDefined, numberToWords } from '@/helpers';
import { getExtraPortions } from '@/services/meal-builder/utils';

@Component({
  name: 'CmgSingleEntreeMealBuilder',
})
export default class CmgMealBuilder extends Vue {
  @Prop({ required: true, type: Object })
  group!: MenuItemGroup;

  @Prop({ required: true, type: Object })
  menu!: Menu;

  @Prop({ required: true, type: Object })
  metadata!: MenuMetadataForChannel;

  @Prop({ required: true, type: Array })
  itemSections!: MealBuilderItemSection[];

  @Prop({ required: true, type: Boolean })
  toggleSectionValidationNavigation!: boolean;

  menuSections: MealBuilderMenuSection[] = [];
  sectionTitleHeight = 64;
  nutritionPrefHeight = 68;
  navTopOffset = 133 - this.sectionTitleHeight;
  additionalDesktopOffeset = 45;
  entreeContainerTop = 0;

  get hasMobileNav(): boolean {
    return this.$services.ui.isMobile();
  }

  get navIsVisible(): boolean {
    return (this.entreeContainerTop < (this.navTopOffset + this.nutritionPrefHeight) && this.hasMobileNav) ? true : false;
  }  

  // Item sections partitioned around the variant section title (so, [everything before "Fillings", everything after "Fillings"] if the title is "Fillings")
  get partitionedItemSections(): [MealBuilderItemSection[], MealBuilderItemSection[]] {
    const variantsSectionMetadata = this.metadata.itemSections[this.variantsItemSectionTitle || ''];
    if (!variantsSectionMetadata) {
      return [[], this.itemSections];
    }

    return partition(this.itemSections, section => isDefined(section.sortOrder) && section.sortOrder < variantsSectionMetadata.sortOrder);
  }

  get preVariantItemSections(): MealBuilderItemSection[] {
    return this.partitionedItemSections[0];
  }

  get postVariantItemSections(): MealBuilderItemSection[] {
    return this.partitionedItemSections[1];
  }

  get builderMode(): BuilderMode | null {
    return this.$appStore.state.mealBuilder.builderMode;
  }

  get selectedVariant(): SelectableVariantMenuItem | null {
    return this.$appStore.state.mealBuilder.selectedVariant;
  }

  get secondaryVariants(): SelectableVariantMenuItem[] {
    if (!this.builderMode || !this.selectedVariant) {
      return [];
    }

    return this.variants.filter(
      v => v.item.itemId != this.selectedVariant!.item.itemId && this.builderMode && this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode, v),
    );
  }

  get selectedItems(): SelectableMenuItem[] {
    return this.$services.mealBuilder.getAllSelectedItems();
  }

  get variants(): SelectableVariantMenuItem[] {
    if (this.$appStore.state.adobeTarget.mealBuilderProteinSortOrder.length > 0) {
      return this.$appStore.state.adobeTarget.mealBuilderProteinSortOrder;
    }

    // Only show customizations for the selected variant
    return this.$appStore.state.mealBuilder.displayedVariants;
  }

  get variantsItemSectionTitle(): string {
    return !this.builderMode ? '' : this.$services.mealBuilder.getVariantsItemSectionTitle(this.builderMode);
  }

  get variantsItemSectionSubtitle(): string | undefined {
    // we can't know the max number of half-portions -- which is used for the subtitle -- until we choose a variant
    // Currently this isnt a problem because all of the varients have the same number of protions allowed. If they data were to change this might be an issue.
    const variant = this.selectedVariant || this.variants[0];
    if (!variant || !this.builderMode) {
      return;
    }

    return this.$services.mealBuilder.getVariantsItemSectionSubtitle(this.builderMode, variant);
  }

  get doubleProteinPortion(): SelectableMenuItem | undefined {
    if (!this.selectedVariant) {
      return;
    }

    const selectedVariants = [this.selectedVariant, ...this.secondaryVariants];
    const extraPortionsForSelectedVariants = getExtraPortions(this.selectedVariant.portions).filter(p =>
      selectedVariants.find(v => p.item.pricingReferenceItemId == v.item.itemId),
    );

    return extraPortionsForSelectedVariants.sort(sortFn(this.itemPricingFieldExtractor, true))[0];
  }

  get doubleProteinSelected(): boolean {
    if (!this.selectedVariant) {
      this.$services.logger.unreachable('no selected variant!');
      return false;
    }

    const extraPortion = this.getExtraPortionCustomizationForVariant(this.selectedVariant);
    return !!(extraPortion && extraPortion.selected);
  }

  get doubleProteinCardDescription(): string {
    if (!this.selectedVariant) {
      return '';
    }

    const filling = joinWithSuffix([this.selectedVariant, ...this.secondaryVariants].sort(sortFn(this.itemPricingFieldExtractor)).map(v => v.item.primaryFillingName), ', ', ' and ');

    return `Double Portion of ${filling}`;
  }

  @Watch('toggleSectionValidationNavigation', { immediate: true })
  async toggleSectionValidationNavigationChanged(toggleSectionValidationNavigation: boolean) {
    const missingSections = this.$appStore.state.mealBuilder.missingItemSections;

    if (missingSections.length) {
      let offset = this.navTopOffset;
      let highestSection = this.findHighestSection(missingSections);

      if (this.hasMobileNav) {
        offset += this.sectionTitleHeight;
      } else {
        offset += this.additionalDesktopOffeset;
      }

      this.$services.ui.scrolltToYPosition(this.menuSections[highestSection].elementRef, offset);
    }
  }

  @Watch('builderMode')
  builderModeChanged(mode: BuilderMode) {
    if(mode) {
      this.setMenuSections();
    }
  }

  mounted() {
    if (this.hasMobileNav) {
      window.addEventListener('scroll', this.calculateEntreeContainerTop);
      this.calculateEntreeContainerTop();
    }
    if(this.builderMode) {
      this.setMenuSections();
    }
  }

  destroyed() {
    if (this.hasMobileNav) {
      window.removeEventListener('scroll', this.calculateEntreeContainerTop);
    }
  }

  getVariantCustomizations(variant: SelectableVariantMenuItem): ItemCardCustomization[] {
    return [...((this.selectedVariant && this.selectedVariant.customizations) || [])];
  }

  getPostVariantItemSectionSubtitle(section: MealBuilderItemSection): string | undefined {
    const maxQuantity = section.maxQuantity ?? 0;

    if (section.id == 'ToppingsContentGroup' && maxQuantity > 1) {
      return `Choose up to ${numberToWords(section.maxQuantity ?? 0)}`;
    }
    return;
  }

  isVariantSelected(variant: SelectableVariantMenuItem): boolean {
    if (!this.builderMode) {
      return false;
    }

    return this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode, variant);
  }

  onVariantSelected(chosenVariant: SelectableVariantMenuItem, args: any) {
    if (!this.builderMode) {
      return;
    }

    this.$services.mealBuilder.selectVariantSafe(this.builderMode, chosenVariant, this.$appStore.state.mealBuilder.selectedVariant, this.variants, args);
  }

  onVariantCustomizationSelected(variant: SelectableVariantMenuItem, customization: ItemCardCustomization) {
    // No matter what variant is selected, we actually want to select the customization on the selected variant (since the customizations are
    // pretty much just proxied to the secondary variants)

    this.onDoubleProteinSelected();
  }

  onItemSelected(args: ItemSelectedEventArgs, itemSection: MealBuilderItemSection) {
    if (!this.builderMode) {
      return;
    }

    const variant = this.selectedVariant || this.$services.mealBuilder.getDefaultVariant(this.builderMode);
    if (!variant) {
      return;
    }

    this.$services.mealBuilder.selectItemSafe(args.item, itemSection, variant, args.args);
  }

  onItemCustomizationSelected(item: SelectableMenuItem, itemSection: MealBuilderItemSection, customization: ItemCardCustomization) {
    const variant = this.selectedVariant || (this.builderMode && this.$services.mealBuilder.getDefaultVariant(this.builderMode));
    if (!variant) {
      this.$services.logger.debug('No variant selected and no default variant found while selecting item customization!');

      return;
    }

    this.$services.mealBuilder.toggleItemCustomizationSafe(item, customization, itemSection, variant);
  }

  onDoubleProteinSelected() {
    this.$services.logger.info('calling onExtraSelectedVariantPortionSelected');

    if (!this.selectedVariant) {
      this.$services.logger.unreachable('no selected variant!');
      return;
    }

    if (!this.builderMode) {
      this.$services.logger.unreachable('no builder mode configured!');
      return;
    }

    const extraPortion = this.getExtraPortionCustomizationForVariant(this.selectedVariant);
    if (!extraPortion) {
      this.$services.logger.error('no extra portion customization found for selectedVariant', { selectedVariant: this.selectedVariant.item.itemId });
      return;
    }

    this.$services.logger.info('selecting extra portion for variant', { extraPortion: extraPortion.item.item.itemId, selectedVariant: this.selectedVariant.item.itemId });
    this.$services.mealBuilder.toggleVariantCustomizationSafe(this.builderMode, this.selectedVariant, this.variants, extraPortion);
  }

  onRewardsExclusiveItemFound(rewardsText: string): void {
    this.$emit('rewards-exclusive-item-found', rewardsText);
  }

  getExtraPortionCustomizationForVariant(variant: SelectableVariantMenuItem): SelectableItemCardCustomization | undefined {
    return variant.customizations.find(c => c.type == ItemCardCustomizationType.SelectableItem && c.portion == PortionItemType.ExtraPortion) as
      | SelectableItemCardCustomization
      | undefined;
  }

  formatItemPrice(menuItem: MenuItem) {
    const { utils, menu } = this.$services;
    return utils.formatPrice(menu.getItemPrice(menuItem), true);
  }

  onSelectNoneSelected(section: MealBuilderItemSection) {
    this.$services.mealBuilder.toggleOptOutOfItemSection(section);
  }

  isSectionOptedOutOf(section: MealBuilderItemSection): boolean {
    return !!this.$appStore.state.mealBuilder.optedOutItemSectionIds.find(id => id == section.id);
  }

  private itemPricingFieldExtractor(selectableItem: SelectableMenuItem) {
    return this.$services.menu.getItemPrice(selectableItem.item);
  }

  private calculateEntreeContainerTop() {
    this.entreeContainerTop = (this.$refs.singleEntreeContainer as HTMLElement).getBoundingClientRect().top;
  }

  private findHighestSection(sectionTitles: string[]) {
    let indices = this.menuSections.map((e, i) => sectionTitles.indexOf(e.title) > -1 ? i : -1).filter(i => i !== -1);
    return Math.min(...indices);
  }

  private setMenuSections() {
    this.menuSections = [];
    let flatPreRefs = this.preVariantItemSections.map((e) => this.$refs[e.title]).flat();
    let flatPostRefs = this.postVariantItemSections.map((e) => this.$refs[e.title]).flat();

    this.preVariantItemSections.map((e,i) => {
      this.menuSections.push({title: e.title, elementRef: flatPreRefs[i] as HTMLElement});
    })

    this.menuSections.push({title: this.variantsItemSectionTitle, elementRef: this.$refs[this.variantsItemSectionTitle] as HTMLElement});

    this.postVariantItemSections.map((e,i) => {
      this.menuSections.push({title: e.title, elementRef: flatPostRefs[i] as HTMLElement});
    })
  }
}
